/**
 * Channel Partner application / profile form: schema, defaults, and the
 * payload builders for `POST /applications` and `PATCH /me`.
 *
 * Mirrored verbatim in apps/provider/src/features/channel-partner/; keep the
 * two copies in sync.
 */
import { z } from 'zod';
import { derivePanType } from '@finmin/utils';
import type {
  ChannelPartnerApplicationPayload,
  ChannelPartnerContext,
  ChannelPartnerOptions,
  ChannelPartnerProfile,
  ChannelPartnerProfileFields,
  ChannelPartnerProfileUpdate,
} from '@/api/channel-partners';

/** The one partner type that needs a free-text description. */
export const OTHERS_PARTNER_TYPE = 'others';

/** Loose GSTIN check: 15 letters/digits. The backend stores GSTIN as given. */
const GSTIN_PATTERN = /^[0-9A-Z]{15}$/;

const normalizeCode = (value: string) => value.replace(/\s+/g, '').toUpperCase();

export const profileFieldsSchema = z.object({
  cp_email: z.string().trim().min(1, 'Email is required').email('Enter a valid email address'),
  partner_type: z.string().min(1, 'Select a partner type'),
  other_partner_type: z.string().trim().max(120, 'Keep this under 120 characters'),
  organisation_name: z.string().trim().max(200, 'Keep this under 200 characters'),
  state: z.string().min(1, 'Select your state'),
  city: z.string().trim().min(1, 'City is required').max(120, 'Keep this under 120 characters'),
  acquisition_source: z.string().trim().max(200, 'Keep this under 200 characters'),
  goals: z.string().trim().max(2000, 'Keep this under 2000 characters'),
  pan: z
    .string()
    .trim()
    .refine((v) => v === '' || derivePanType(v) !== null, 'Enter a valid PAN, e.g. ABCDE1234F'),
  gstin: z
    .string()
    .trim()
    .refine((v) => v === '' || GSTIN_PATTERN.test(normalizeCode(v)), 'Enter a valid 15-character GSTIN'),
  pan_document_id: z.string().nullable(),
  gst_document_id: z.string().nullable(),
});

function requireOtherType(
  values: { partner_type: string; other_partner_type: string },
  ctx: z.RefinementCtx,
) {
  if (values.partner_type === OTHERS_PARTNER_TYPE && !values.other_partner_type.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['other_partner_type'],
      message: 'Describe your partner type',
    });
  }
}

export const profileFormSchema = profileFieldsSchema.superRefine(requireOtherType);
export type ProfileFormInput = z.infer<typeof profileFieldsSchema>;

export const applicationFormSchema = profileFieldsSchema
  .extend({
    agreement_accepted: z
      .boolean()
      .refine((v) => v, 'Accept the General Agreement to continue'),
  })
  .superRefine(requireOtherType);
export type ApplicationFormInput = z.infer<typeof applicationFormSchema>;

// ---------- defaults --------------------------------------------------------

/** Form values from a saved record, dropping choices the backend no longer offers. */
function valuesFrom(
  record: Partial<ChannelPartnerProfileFields> | null | undefined,
  options: Pick<ChannelPartnerOptions, 'partner_types' | 'states'> | undefined,
): ProfileFormInput {
  const partnerType = record?.partner_type ?? '';
  const state = record?.state ?? '';
  const typeOffered = !options || options.partner_types.some((t) => t.id === partnerType);
  const stateOffered = !options || options.states.includes(state);
  return {
    cp_email: record?.cp_email ?? '',
    partner_type: typeOffered ? partnerType : '',
    other_partner_type: record?.other_partner_type ?? '',
    organisation_name: record?.organisation_name ?? '',
    state: stateOffered ? state : '',
    city: record?.city ?? '',
    acquisition_source: record?.acquisition_source ?? '',
    goals: record?.goals ?? '',
    pan: record?.pan ?? '',
    gstin: record?.gstin ?? '',
    pan_document_id: record?.pan_document_id ?? null,
    gst_document_id: record?.gst_document_id ?? null,
  };
}

/**
 * Prefill for a new submission: an invitation draft or an earlier (rejected)
 * application supplies the fields; the CP email falls back to the account
 * email. The agreement is always accepted afresh.
 */
export function applicationDefaults(
  context: ChannelPartnerContext | undefined,
  options?: Pick<ChannelPartnerOptions, 'partner_types' | 'states'>,
): ApplicationFormInput {
  const values = valuesFrom(context?.application, options);
  return {
    ...values,
    cp_email: values.cp_email || context?.account.email || '',
    agreement_accepted: false,
  };
}

export function profileDefaults(
  profile: ChannelPartnerProfile | null | undefined,
  options?: Pick<ChannelPartnerOptions, 'partner_types' | 'states'>,
): ProfileFormInput {
  return valuesFrom(profile, options);
}

// ---------- payload builders ------------------------------------------------

type EditableField = Exclude<keyof ChannelPartnerProfileFields, 'pan_type'>;

const EDITABLE_FIELDS: readonly EditableField[] = [
  'cp_email',
  'partner_type',
  'other_partner_type',
  'organisation_name',
  'state',
  'city',
  'acquisition_source',
  'goals',
  'pan',
  'gstin',
  'pan_document_id',
  'gst_document_id',
];

/** Trimmed, upper-cased where the backend expects it; empty → null. */
function normalizedValues(values: ProfileFormInput): Record<EditableField, string | null> {
  const text = (v: string) => v.trim() || null;
  const isOthers = values.partner_type === OTHERS_PARTNER_TYPE;
  return {
    cp_email: values.cp_email.trim(),
    partner_type: values.partner_type,
    // Only meaningful for "Others"; a stale description is cleared.
    other_partner_type: isOthers ? text(values.other_partner_type) : null,
    organisation_name: text(values.organisation_name),
    state: values.state,
    city: values.city.trim(),
    acquisition_source: text(values.acquisition_source),
    goals: text(values.goals),
    pan: values.pan.trim() ? normalizeCode(values.pan) : null,
    gstin: values.gstin.trim() ? normalizeCode(values.gstin) : null,
    pan_document_id: values.pan_document_id || null,
    gst_document_id: values.gst_document_id || null,
  };
}

/**
 * `POST /applications` body. Optional fields are omitted rather than sent
 * empty; `agreement_accepted` is the literal `true` the backend requires.
 */
export function buildApplicationPayload(
  values: ApplicationFormInput,
  agreementId: string,
): ChannelPartnerApplicationPayload {
  const normalized = normalizedValues(values);
  const optional = Object.fromEntries(
    (
      [
        'other_partner_type',
        'organisation_name',
        'acquisition_source',
        'goals',
        'pan',
        'gstin',
        'pan_document_id',
        'gst_document_id',
      ] as const
    )
      .filter((key) => normalized[key] !== null)
      .map((key) => [key, normalized[key] as string]),
  );
  return {
    cp_email: normalized.cp_email as string,
    partner_type: normalized.partner_type as string,
    state: normalized.state as string,
    city: normalized.city as string,
    ...optional,
    agreement_id: agreementId,
    agreement_accepted: true,
  };
}

/**
 * `PATCH /me` body: only the fields that differ from the saved profile, with
 * `null` clearing an optional one. Empty object = nothing to save.
 */
export function buildProfileUpdate(
  profile: ChannelPartnerProfile,
  values: ProfileFormInput,
): ChannelPartnerProfileUpdate {
  const next = normalizedValues(values);
  const changes: ChannelPartnerProfileUpdate = {};
  for (const key of EDITABLE_FIELDS) {
    const before = profile[key] ?? null;
    if (next[key] !== before) changes[key] = next[key];
  }
  return changes;
}
